import React, { Component, Fragment } from 'react';
import { get, getOr, set, has } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import PathKeyCell from '../../widgets/custom-cell/PathKey';
import DateCell from '../../widgets/custom-cell/Date';
import InventoryActions from '../../../constants/inventory/InventoryActions';
import Pagination from '../../Pagination';
import {
  MainActionLabels,
  AddActionLabels,
  RemoveActionLabels
} from '../../../constants/inventory/Reasons';
import PaginationType from '../../../interfaces/PaginationType';
import HeaderCell from '../../widgets/custom-cell/HeaderCell';
import { TagsCell } from '../ItemList/CustomCells';
import AutoHeightTable from '../../AutoHeightTable';

const { Column, Cell } = Table;

const ReasonLabels = {
  ...AddActionLabels,
  ...RemoveActionLabels,
  [InventoryActions.MOVE]: 'Move'
};

const InventoryActionCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {get(rowData[dataKey], MainActionLabels)}
  </Cell>
);

InventoryActionCell.propTypes = {
  rowData: PropTypes.shape({}),
  dataKey: PropTypes.string.isRequired
};

InventoryActionCell.defaultProps = {
  rowData: null
};

const InventoryReasonCell = ({ rowData: { category }, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {getOr('Unknown', category, ReasonLabels)}
  </Cell>
);

InventoryReasonCell.propTypes = {
  rowData: PropTypes.shape({
    action: PropTypes.number,
    category: PropTypes.number
  }),
  dataKey: PropTypes.string.isRequired
};

InventoryReasonCell.defaultProps = {
  rowData: null
};

const UserCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {get(`${dataKey}.firstname`, rowData)} {get(`${dataKey}.lastname`, rowData)}
  </Cell>
);

UserCell.propTypes = {
  rowData: PropTypes.shape({}),
  dataKey: PropTypes.string.isRequired
};

UserCell.defaultProps = {
  rowData: null
};

class InventoryAuditLogList extends Component {
  componentDidMount() {
    const { onInit, isFetching } = this.props;
    if (!isFetching) {
      onInit();
    }
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;
    onUnmount();
  }

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  renderPagination() {
    const { pagination } = this.props;
    return (
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={this.handleChangePage}
        onChangeLimit={this.handleChangeLimit}
      />
    );
  }

  render() {
    const { auditLogs, showItemColumn, isFetching } = this.props;
    return (
      <Fragment>
        <AutoHeightTable
          reduceHeight={540}
          data={auditLogs}
          loading={isFetching}
          bordered
        >
          <Column width={60} fixed>
            <HeaderCell>Id</HeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column minWidth={100} flexGrow={1}>
            <HeaderCell>User</HeaderCell>
            <UserCell dataKey="user" />
          </Column>
          <Column width={100} resizable>
            <HeaderCell>Action</HeaderCell>
            <InventoryActionCell dataKey="action" />
          </Column>
          <Column width={146} resizable>
            <HeaderCell>Reason</HeaderCell>
            <InventoryReasonCell dataKey="category" />
          </Column>
          <Column width={70} resizable align="right">
            <HeaderCell>Qty</HeaderCell>
            <Cell dataKey="quantity" />
          </Column>
          {showItemColumn && (
            <Column width={100} resizable>
              <HeaderCell>Item</HeaderCell>
              <PathKeyCell dataKey="item.name" />
            </Column>
          )}
          <Column width={120} resizable>
            <HeaderCell>Location</HeaderCell>
            <PathKeyCell dataKey="location.name" />
          </Column>
          <Column width={120} resizable>
            <HeaderCell>Zone</HeaderCell>
            <PathKeyCell dataKey="zone.name" />
          </Column>
          <Column width={200}>
            <HeaderCell>Tags</HeaderCell>
            <TagsCell dataKey="tags" />
          </Column>
          <Column width={240} resizable>
            <HeaderCell>Note</HeaderCell>
            <Cell dataKey="note" />
          </Column>
          <Column width={130} resizable>
            <HeaderCell>Date Performed</HeaderCell>
            <DateCell dataKey="performedOn" />
          </Column>
          <Column width={108} resizable>
            <HeaderCell>Date Created</HeaderCell>
            <DateCell dataKey="createdAt" />
          </Column>
        </AutoHeightTable>
        {this.renderPagination()}
      </Fragment>
    );
  }
}

InventoryAuditLogList.propTypes = {
  showItemColumn: PropTypes.bool,
  auditLogs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  isFetching: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onUnmount: PropTypes.func
};

InventoryAuditLogList.defaultProps = {
  showItemColumn: true,
  onUnmount: () => {}
};

export default InventoryAuditLogList;
